import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { tournamentAPI, playerAPI, contestAPI, teeSheetAPI } from '../../services/api';
import './TournamentLayout.css';

function TournamentLayout() {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [tournament, setTournament] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rosterCount, setRosterCount] = useState(0);
  const [contestCount, setContestCount] = useState(0);
  const [hasTeeSheet, setHasTeeSheet] = useState(false);
  const [onEditTournament, setOnEditTournament] = useState(null);

  useEffect(() => {
    if (tournamentId === 'demo') {
      setTournament({
        name: 'Demo Tournament',
        date: new Date().toISOString(),
        status: 'demo',
        version: 1
      });
      setIsLoading(false);
    } else {
      loadTournament();
    }
  }, [tournamentId]);

  // Auto-navigate to default tab when tournament data loads
  useEffect(() => {
    if (tournament && !isLoading) {
      const currentPath = location.pathname;
      const basePath = `/editor/${tournamentId}`;
      
      // If we're at the base tournament path, redirect to tee sheet by default
      if (currentPath === basePath || currentPath === `${basePath}/`) {
        navigateToTab('teesheet');
      }
    }
  }, [tournament, isLoading, location.pathname, tournamentId]);

  const loadTournament = async () => {
    try {
      setIsLoading(true);
      const response = await tournamentAPI.getById(tournamentId);
      setTournament(response.data);
      
      // Load counts after tournament data is loaded
      await loadCounts();
    } catch (err) {
      console.error('Error loading tournament:', err);
      setError('Failed to load tournament data.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadCounts = async () => {
    try {
      // Load roster count
      const playersResponse = await playerAPI.getByTournament(tournamentId);
      setRosterCount(playersResponse.data?.length || 0);

      // Load contest count  
      const contestsResponse = await contestAPI.getByTournament(tournamentId);
      setContestCount(contestsResponse.data?.length || 0);

      // Check if tee sheet actually exists
      try {
        const teeSheetResponse = await teeSheetAPI.getByTournament(tournamentId);
        const teeSheetData = teeSheetResponse.data;
        setHasTeeSheet(teeSheetData && teeSheetData.groups && teeSheetData.groups.length > 0);
      } catch (teeSheetErr) {
        // Tee sheet doesn't exist yet
        setHasTeeSheet(false);
      }
    } catch (err) {
      console.error('Error loading counts:', err);
      // Don't set error state for counts, just log it
    }
  };

  const getCurrentTab = () => {
    const path = location.pathname;
    if (path.includes('/roster')) return 'roster';
    if (path.includes('/rules')) return 'rules';
    if (path.includes('/waitlist')) return 'waitlist';
    if (path.includes('/last-minute-invites')) return 'last-minute-invites';
    if (path.includes('/contests')) return 'contests';
    if (path.includes('/feedback')) return 'feedback';
    if (path.includes('/portal')) return 'portal';
    if (path.includes('/teesheet')) return 'teesheet';

    // Base path (no sub-route) defaults to teesheet
    const basePath = `/editor/${tournamentId}`;
    if (path === basePath || path === `${basePath}/`) {
      return 'teesheet';
    }

    // Default fallback
    return 'teesheet';
  };

  const navigateToTab = (tab) => {
    const basePath = `/editor/${tournamentId}`;
    switch (tab) {
      case 'teesheet':
        navigate(basePath);
        break;
      case 'roster':
        navigate(`${basePath}/roster`);
        break;
      case 'rules':
        navigate(`${basePath}/rules`);
        break;
      case 'waitlist':
        navigate(`${basePath}/waitlist`);
        break;
      case 'last-minute-invites':
        navigate(`${basePath}/last-minute-invites`);
        break;
      case 'contests':
        navigate(`${basePath}/contests`);
        break;
      case 'feedback':
        navigate(`${basePath}/feedback`);
        break;
      case 'portal':
        navigate(`${basePath}/portal`);
        break;
      default:
        navigate(basePath);
    }
  };

  if (isLoading) {
    return (
      <div className="tournament-layout loading">
        <div className="loading-spinner">Loading tournament...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tournament-layout error">
        <div className="error-message">
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
        </div>
      </div>
    );
  }

  const currentTab = getCurrentTab();

  return (
    <div className="tournament-layout">
      <header className="tournament-header">
        <div className="tournament-info">
          <div className="tournament-name-container">
            <h1 className="tournament-name">
              {tournament?.name || 'Tournament Management'}
            </h1>
            <button 
              className="edit-icon-button"
              title="Edit tournament settings"
              onClick={onEditTournament}
            >
              ✏️
            </button>
          </div>
          
          <div className="tournament-details">
            {tournament?.date && (
              <span className="tournament-date">
                {new Date(tournament.date).toLocaleDateString()}
              </span>
            )}
            {tournament?.status && (
              <span className={`tournament-status status-${tournament.status}`}>
                {tournament.status.replace('-', ' ')}
              </span>
            )}
            {tournament?.feedbackDeadline && (
              <span className="feedback-deadline">
                Feedback due: {new Date(tournament.feedbackDeadline).toLocaleString()}
              </span>
            )}
          </div>
        </div>

        <div className="tournament-actions">
          <button 
            onClick={() => navigate('/')}
            className="back-button"
          >
            ← Back to Tournaments
          </button>
        </div>
      </header>

      <nav className="tournament-nav">
        <div
          className={`nav-item ${currentTab === 'roster' ? 'active' : ''}`}
          onClick={() => navigateToTab('roster')}
        >
          Roster
          {rosterCount > 0 && <span className="nav-badge">{rosterCount}</span>}
        </div>
        <div
          className={`nav-item ${currentTab === 'rules' ? 'active' : ''}`}
          onClick={() => navigateToTab('rules')}
        >
          Rules
        </div>
        <div
          className={`nav-item ${currentTab === 'teesheet' ? 'active' : ''}`}
          onClick={() => navigateToTab('teesheet')}
        >
          Tee Sheet
        </div>
        <div
          className={`nav-item ${currentTab === 'waitlist' ? 'active' : ''}`}
          onClick={() => navigateToTab('waitlist')}
        >
          Waitlist
        </div>
        <div
          className={`nav-item ${currentTab === 'last-minute-invites' ? 'active' : ''}`}
          onClick={() => navigateToTab('last-minute-invites')}
        >
          🕐 Last-Minute Invites
        </div>
        <div 
          className={`nav-item ${currentTab === 'contests' ? 'active' : ''}`}
          onClick={() => navigateToTab('contests')}
        >
          Contests
          {contestCount > 0 && <span className="nav-badge">{contestCount}</span>}
        </div>
        <div
          className={`nav-item ${currentTab === 'feedback' ? 'active' : ''}`}
          onClick={() => navigateToTab('feedback')}
        >
          Feedback
        </div>
        <div
          className={`nav-item ${currentTab === 'portal' ? 'active' : ''}`}
          onClick={() => navigateToTab('portal')}
        >
          Portal
        </div>
      </nav>

      <main className="tournament-content">
        <Outlet context={{ tournament, setTournament, refreshCounts: loadCounts, setOnEditTournament }} />
      </main>
    </div>
  );
}

export default TournamentLayout;